import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  Database,
  Key,
  Rocket,
  Upload,
  UserCog,
  UserPlus,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import usePublishStatus from '../../hooks/usePublishStatus';
import { get } from '../../services/api/client';
import adminApi from '../../services/api/admin';
import { listDeployments } from '../../services/deploymentsApi';

import CockpitHome from './CockpitHome';
import { CockpitPanel, CockpitTabs, CockpitWorkspaceContent } from './cockpit';
import { adminRoutes } from './navigation/adminRoutes';
import { getRememberedCockpitPath } from './navigation/adminPlaceMemory';
import { canAccessSection, getAdminUser } from './navigation/sectionAccess';

const API_KEY_SERVICES = [
  'fal',
  'anthropic',
  'google_places',
  'google_tts',
  'openai',
  'meta',
  'tiktok',
];

const emptySnapshot = {
  users: null,
  apiKeys: null,
  lastBackup: null,
  lastDeployment: null,
};

const formatDateTime = (value) => {
  if (!value) return 'Never';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown time';

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const formatStatus = (status) => {
  if (!status) return 'No activity';
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const summarizeRoles = (users) => {
  if (!users?.length) return 'No roles assigned';

  const counts = users.reduce((summary, user) => {
    const role = user.role || (user.is_protected ? 'owner' : 'admin');
    summary[role] = (summary[role] || 0) + 1;
    return summary;
  }, {});

  return Object.entries(counts)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([role, count]) => `${count} ${role}${count === 1 ? '' : 's'}`)
    .join(' • ');
};

const loadDeployment = async () => {
  const data = await listDeployments(1);
  return data.deployments?.[0] || null;
};

const SettingsCockpitHome = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const adminUser = useMemo(() => getAdminUser(), []);
  const hasAccess = useCallback(
    (sectionId) => canAccessSection(sectionId, adminUser),
    [adminUser]
  );
  const sections = useMemo(
    () => adminRoutes.settings.sections.filter((section) => hasAccess(section.id)),
    [hasAccess]
  );
  const normalizedPath = location.pathname.replace(/\/+$/, '') || '/';
  const activeSection = sections.find((section) => section.path === normalizedPath);
  const [snapshot, setSnapshot] = useState(emptySnapshot);
  const [loadingSnapshot, setLoadingSnapshot] = useState(true);
  const [snapshotErrors, setSnapshotErrors] = useState([]);
  const {
    hasPendingChanges,
    lastPublishedAt,
    pendingChanges,
    loading: publishLoading,
    error: publishError,
    refresh: refreshPublish,
  } = usePublishStatus();

  const loadSnapshot = useCallback(async () => {
    setLoadingSnapshot(true);

    const requests = [
      ['users', hasAccess('adminusers'), () => adminApi.adminUsers.getAll()],
      ['apiKeys', hasAccess('apikeys'), () => get('/admin/api-keys')],
      ['lastBackup', hasAccess('backups'), async () => {
        const response = await adminApi.backups.list({ limit: 20 });
        return response.backups?.find((backup) => backup.status === 'completed') || null;
      }],
      ['lastDeployment', hasAccess('deployments'), loadDeployment],
    ];

    const permittedRequests = requests.filter(([, permitted]) => permitted);
    const results = await Promise.allSettled(
      permittedRequests.map(([, , request]) => Promise.resolve().then(request))
    );
    const nextSnapshot = { ...emptySnapshot };
    const errors = [];

    results.forEach((result, index) => {
      const [key] = permittedRequests[index];
      if (result.status === 'fulfilled') {
        nextSnapshot[key] = result.value;
      } else {
        errors.push(key);
      }
    });

    setSnapshot(nextSnapshot);
    setSnapshotErrors(errors);
    setLoadingSnapshot(false);
  }, [hasAccess]);

  useEffect(() => {
    loadSnapshot();
  }, [loadSnapshot]);

  const configuredKeyCount = API_KEY_SERVICES.filter(
    (service) => snapshot.apiKeys?.[`${service}_configured`]
  ).length;
  const deploymentNeedsAttention = ['failed', 'tests_failed', 'remediation_error'].includes(
    snapshot.lastDeployment?.status
  );

  const kpis = useMemo(() => {
    const items = [];

    if (hasAccess('adminusers')) {
      items.push({
        id: 'admin-users',
        title: 'Admin users',
        value: loadingSnapshot ? '—' : snapshot.users?.length ?? 'Unavailable',
        subtitle: summarizeRoles(snapshot.users),
        icon: UserCog,
      });
    }

    if (hasAccess('apikeys')) {
      items.push({
        id: 'api-keys',
        title: 'API key status',
        value: loadingSnapshot ? '—' : snapshot.apiKeys ? `${configuredKeyCount}/${API_KEY_SERVICES.length}` : 'Unavailable',
        subtitle: snapshot.apiKeys
          ? `${API_KEY_SERVICES.length - configuredKeyCount} service${API_KEY_SERVICES.length - configuredKeyCount === 1 ? '' : 's'} need configuration`
          : 'Could not load key health',
        icon: Key,
        tone: configuredKeyCount === API_KEY_SERVICES.length
          ? 'good'
          : 'warn',
      });
    }

    if (hasAccess('publish')) {
      items.push({
        id: 'publish',
        title: 'Publish status',
        value: publishLoading ? '—' : hasPendingChanges ? `${pendingChanges.length} pending` : 'Current',
        subtitle: `Last publish: ${formatDateTime(lastPublishedAt)}`,
        icon: Upload,
        tone: hasPendingChanges
          ? 'warn'
          : 'good',
      });
    }

    if (hasAccess('deployments')) {
      items.push({
        id: 'deployment',
        title: 'Last deployment',
        value: loadingSnapshot ? '—' : formatStatus(snapshot.lastDeployment?.status),
        subtitle: snapshot.lastDeployment
          ? formatDateTime(snapshot.lastDeployment.created_at)
          : 'No deployment history available',
        icon: Rocket,
        tone: deploymentNeedsAttention
          ? 'danger'
          : undefined,
      });
    }

    if (hasAccess('backups')) {
      items.push({
        id: 'backup',
        title: 'Last successful backup',
        value: loadingSnapshot ? '—' : snapshot.lastBackup ? 'Completed' : 'No backup',
        subtitle: snapshot.lastBackup
          ? formatDateTime(snapshot.lastBackup.completed_at || snapshot.lastBackup.started_at)
          : 'No successful backup found',
        icon: Database,
        tone: snapshot.lastBackup
          ? 'good'
          : 'warn',
      });
    }

    return items;
  }, [
    configuredKeyCount,
    deploymentNeedsAttention,
    hasAccess,
    hasPendingChanges,
    lastPublishedAt,
    loadingSnapshot,
    pendingChanges.length,
    publishLoading,
    snapshot,
  ]);

  const quickActions = useMemo(() => [
    { id: 'add-admin', section: 'adminusers', label: 'Add admin', icon: UserPlus, path: '/admin/settings/users' },
    { id: 'publish', section: 'publish', label: 'Publish', icon: Upload, path: '/admin/settings/publish' },
    { id: 'deploy', section: 'deployments', label: 'Trigger deploy', icon: Rocket, path: '/admin/settings/deployments' },
    { id: 'backup', section: 'backups', label: 'Run backup', icon: Database, path: '/admin/settings/backups' },
  ].filter((action) => hasAccess(action.section)), [hasAccess]);

  const failedSources = [
    ...snapshotErrors,
    ...(publishError ? ['publish'] : []),
  ];

  const handleRefresh = () => {
    loadSnapshot();
    refreshPublish();
  };

  if (!activeSection) {
    return sections[0]
      ? <Navigate to={getRememberedCockpitPath('settings', sections, adminUser)} replace />
      : <Navigate to="/admin" replace />;
  }

  const panelId = `settings-cockpit-tab-panel-${activeSection.id}`;

  return (
    <CockpitHome
      categoryId="settings"
      description="Manage access, integrations, publishing, and platform operations."
      kpis={kpis}
      quickActions={quickActions}
      canAccessSection={hasAccess}
      showSectionLinks={false}
      alerts={failedSources.length > 0 && (
        <Card className="border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 text-amber-700 dark:text-amber-400" />
              <div>
                <p className="font-medium text-amber-900 dark:text-amber-200">
                  Some platform metrics are unavailable
                </p>
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  Other dashboard data is still live. Retry to refresh the missing metrics.
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              Retry metrics
            </Button>
          </CardContent>
        </Card>
      )}
    >
      <CockpitTabs
        tabs={sections}
        active={activeSection.id}
        onChange={(sectionId) => {
          const section = sections.find((candidate) => candidate.id === sectionId);
          if (section) navigate(section.path);
        }}
        label="Settings and platform sections"
        idPrefix="settings-cockpit-tab"
      />

      <CockpitPanel
        id={panelId}
        aria-labelledby={`settings-cockpit-tab-${activeSection.id}`}
      >
        <CockpitWorkspaceContent>
          <Outlet context={{ activeSection, activeSubsection: null }} />
        </CockpitWorkspaceContent>
      </CockpitPanel>
    </CockpitHome>
  );
};

export default SettingsCockpitHome;
